import { CacheEntities, getFlaggedIdsWithCache } from "@punchup/utils/caches";

import { ShowWithComedian } from "./shows";
import { getApiClient } from "@punchup/utils/apiRegistry";
import { logException } from "@punchup/sentry";

interface AvailabilityResult {
  eventId: string;
  soldOut: boolean;
  // Admin-only switch from ticketing-api (Event.inactive). When true the show is
  // meant to be invisible everywhere public: excluded from listings, 404 on a direct
  // fetch, and rejected at checkout. downstroke drops such shows from public listings.
  inactive?: boolean;
  error?: string;
}

interface BulkAvailabilityResponse {
  results: AvailabilityResult[];
}

interface PasswordProtectionResult {
  eventId: string;
  isPasswordProtected: boolean;
  hidden: boolean;
  error?: string;
}

interface BulkPasswordProtectionResponse {
  results: PasswordProtectionResult[];
}

/**
 * Applies one `bulk-availability` response to a list of shows:
 *
 * - Sold-out override: Tixologi's live availability is OR-ed with the show's
 *   own `is_sold_out` column in Supabase. If the Supabase flag is `true` the
 *   show is forced sold-out (Punchup shows "Join Waitlist") regardless of what
 *   Tixologi reports — this is the manual lever for events whose remaining
 *   seats exist but are not publicly sellable (e.g. held / not-for-sale in
 *   Seats.io). It is one-way: it can force sold-out, never force available.
 *   The Tixologi checkout widget reads its own ticket-type flags and is NOT
 *   affected, so a forced-waitlist event still sells through the direct widget
 *   link.
 * - Inactive events are dropped: an admin-only event must be invisible on
 *   every public surface, and the same response already carries the flag.
 */
function applyAvailability(
  shows: ShowWithComedian[],
  results: AvailabilityResult[]
): ShowWithComedian[] {
  const soldOutByEventId = new Map<string, boolean>();
  const inactiveEventIds = new Set<string>();
  results.forEach((result) => {
    if (result.error) {
      return;
    }
    soldOutByEventId.set(result.eventId, result.soldOut);
    if (result.inactive) {
      inactiveEventIds.add(result.eventId);
    }
  });

  return shows.flatMap((show) => {
    const eventId = show.tixologi_event_id;
    if (!eventId || !soldOutByEventId.has(eventId)) {
      return [show];
    }
    if (inactiveEventIds.has(eventId)) {
      return [];
    }
    return [
      {
        ...show,
        is_sold_out: show.is_sold_out || soldOutByEventId.get(eventId)!,
      },
    ];
  });
}

/**
 * Overlays live sold-out status and drops inactive events (see
 * applyAvailability) by calling Tixologi directly. Use in server components.
 * Fails open: on any error the shows come back unchanged.
 */
export async function updateTixologiAvailabilityServer(
  shows: ShowWithComedian[]
): Promise<ShowWithComedian[]> {
  const eventIds = tixologiEventIds(shows);
  if (eventIds.length === 0) {
    return shows;
  }

  try {
    return applyAvailability(shows, await fetchAvailabilityServer(eventIds));
  } catch (error) {
    logException(`Error fetching tixologi availability: ${error}`);
    return shows;
  }
}

/**
 * Client-component variant of [[updateTixologiAvailabilityServer]] — reaches
 * the same response through downstroke's availability passthrough route.
 */
export async function updateTixologiAvailabilityClient(
  shows: ShowWithComedian[]
): Promise<ShowWithComedian[]> {
  const eventIds = tixologiEventIds(shows);
  if (eventIds.length === 0) {
    return shows;
  }

  try {
    return applyAvailability(shows, await fetchAvailabilityClient(eventIds));
  } catch (error) {
    logException(`Error fetching tixologi availability: ${error}`);
    return shows;
  }
}

/**
 * Given an array of shows, filters out shows that are password protected or hidden.
 * It does so by looking up in tixologi if any tixologi shows from the original array are password protected or hidden
 * and excludes them from the returned array.
 *
 * Use this in server components for better performance.
 */
export async function filterOutPasswordProtectedShowsServer(
  shows: ShowWithComedian[]
): Promise<ShowWithComedian[]> {
  const tixologiShows = shows.filter((show) => show.tixologi_event_id);

  if (tixologiShows.length === 0) {
    return shows;
  }

  const baseUrl = process.env.NEXT_PUBLIC_TIXOLOGI_API_URL;
  if (!baseUrl) {
    return shows;
  }

  try {
    const eventIds = tixologiShows.map((show) => show.tixologi_event_id);

    const response = await fetch(
      `${baseUrl}/public/ticketing/bulk-password-protection`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ eventIds }),
      }
    );

    if (!response.ok) {
      const err = new Error(
        "[Tixologi] password protected shows request failed"
      );
      logException(err, { tags: { tixologi: true } });
      return shows;
    }

    const data: BulkPasswordProtectionResponse = await response.json();
    const passwordProtectionMap = new Map<
      string,
      { isPasswordProtected: boolean; hidden: boolean }
    >();
    data.results.forEach((result) => {
      if (!result.error) {
        passwordProtectionMap.set(result.eventId, {
          isPasswordProtected: result.isPasswordProtected,
          hidden: result.hidden,
        });
      }
    });

    return shows.filter((show) => {
      if (!show.tixologi_event_id) {
        return true;
      }
      const protectionStatus = passwordProtectionMap.get(
        show.tixologi_event_id
      );
      if (!protectionStatus) {
        return true;
      }
      // Filter out shows that are password protected OR hidden
      return !protectionStatus.isPasswordProtected && !protectionStatus.hidden;
    });
  } catch (error) {
    logException(`Error fetching tixologi password protection: ${error}`);
    return shows;
  }
}

function tixologiEventIds(
  shows: Pick<ShowWithComedian, "tixologi_event_id">[]
) {
  return shows
    .map((show) => show.tixologi_event_id)
    .filter((id): id is string => Boolean(id));
}

async function fetchAvailabilityServer(
  eventIds: string[],
  signal?: AbortSignal
): Promise<AvailabilityResult[]> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/public/ticketing/bulk-availability`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventIds }),
      signal,
    }
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch tixologi availability: ${response.status}`
    );
  }
  const data: BulkAvailabilityResponse = await response.json();
  return data.results;
}

async function fetchAvailabilityClient(
  eventIds: string[]
): Promise<AvailabilityResult[]> {
  const data = await getApiClient().post<BulkAvailabilityResponse>(
    "/api/v2/public/ticketing/availability",
    { eventIds }
  );
  return data.results;
}

/**
 * Drops shows whose linked tixologi event is inactive (admin-only), for the
 * routes that list shows without the sold-out overlay. Surfaces that already
 * call updateTixologiAvailabilityServer get this for free — do not stack both.
 * Fails open: on any error the shows come back unchanged.
 */
export async function filterOutInactiveShowsServer(
  shows: ShowWithComedian[]
): Promise<ShowWithComedian[]> {
  const eventIds = tixologiEventIds(shows);
  if (eventIds.length === 0) {
    return shows;
  }
  try {
    const inactive = await fetchInactiveEventIds(eventIds);
    return shows.filter(
      (show) => !show.tixologi_event_id || !inactive.has(show.tixologi_event_id)
    );
  } catch (error) {
    logException(`Error fetching tixologi inactive status: ${error}`);
    return shows;
  }
}

export const INACTIVE_LOOKUP_CACHE_TTL_SECONDS = 60 * 60;

/**
 * Batch-job variant of [[filterOutInactiveShowsServer]] for callers that run
 * the same lookup once per user (the Thursday digest). Each event id reaches
 * bulk-availability at most once per INACTIVE_LOOKUP_CACHE_TTL_SECONDS across
 * every caller, through a shared Redis memo of the flag. Request-time public
 * surfaces keep the uncached filter so an unpublish is immediate there.
 * Fails open like the uncached filter.
 */
export async function filterOutInactiveShowsCachedServer(
  shows: ShowWithComedian[]
): Promise<ShowWithComedian[]> {
  const eventIds = tixologiEventIds(shows);
  if (eventIds.length === 0) {
    return shows;
  }
  try {
    const inactive = await getFlaggedIdsWithCache({
      entity: CacheEntities.TIXOLOGI_EVENT_INACTIVE,
      ids: eventIds,
      ttl: INACTIVE_LOOKUP_CACHE_TTL_SECONDS,
      fetchFlagged: (missingIds) => fetchInactiveEventIds(missingIds),
    });
    return shows.filter(
      (show) => !show.tixologi_event_id || !inactive.has(show.tixologi_event_id)
    );
  } catch (error) {
    logException(`Error fetching tixologi inactive status: ${error}`);
    return shows;
  }
}

/**
 * Single-show guard for the detail pages (`/show`, `/event`, `/e`), which fetch a
 * show straight by id and would otherwise render an admin-only show publicly.
 * A show with no `tixologi_event_id` is never inactive, and a failed lookup
 * fails open (false) so the page renders as before.
 */
export async function isShowInactiveServer(
  show: Pick<ShowWithComedian, "tixologi_event_id">
): Promise<boolean> {
  if (!show.tixologi_event_id) {
    return false;
  }
  try {
    const inactive = await fetchInactiveEventIds([show.tixologi_event_id]);
    return inactive.has(show.tixologi_event_id);
  } catch (error) {
    logException(`Error fetching tixologi inactive status: ${error}`);
    return false;
  }
}

// Bounds the admin show list's wait on bulk-availability: past this, the list renders
// without Unpublished badges instead of hanging. Admin path only — the public filter has
// no timeout, because failing open there would show unpublished shows to the public.
export const ADMIN_INACTIVE_LOOKUP_TIMEOUT_MS = 3000;

/**
 * The set of the given tixologi event ids that are marked inactive (admin-only),
 * read live from bulk-availability. Unlike filterOutInactiveShowsServer (which DROPS
 * inactive shows for public surfaces), this returns the ids so an ADMIN surface can
 * KEEP the show and flag it (the "Unpublished" badge). Fails open: a lookup error, or
 * no answer within ADMIN_INACTIVE_LOOKUP_TIMEOUT_MS, yields an empty set, so the caller
 * degrades to "none flagged" rather than throwing or hanging.
 */
export async function getInactiveEventIdsServer(
  eventIds: string[]
): Promise<Set<string>> {
  if (eventIds.length === 0) {
    return new Set();
  }
  try {
    return await fetchInactiveEventIds(
      eventIds,
      AbortSignal.timeout(ADMIN_INACTIVE_LOOKUP_TIMEOUT_MS)
    );
  } catch (error) {
    logException(`Error fetching tixologi inactive status: ${error}`);
    return new Set();
  }
}

async function fetchInactiveEventIds(eventIds: string[], signal?: AbortSignal) {
  const results = await fetchAvailabilityServer(eventIds, signal);
  return new Set(
    results
      .filter((result) => !result.error && result.inactive)
      .map((result) => result.eventId)
  );
}
